// src/pages/EmailPreferencesPage.js
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Alert, Form } from 'react-bootstrap';
import { apiClient } from '../services/api';
import FroggySpinner from '../components/common/FroggySpinner';

const EmailPreferencesPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [didUnsubscribe, setDidUnsubscribe] = useState(false);

  const fetchPreferences = useCallback(async (unsubscribe = false) => {
    try {
      const params = unsubscribe ? '?unsubscribe=1' : '';
      const res = await apiClient.get(`/marketing/preferences/${token}/${params}`);
      setPreferences(res.data);
      if (unsubscribe) {
        setDidUnsubscribe(true);
        setMessage('You have been unsubscribed from marketing emails.');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This unsubscribe link is invalid or has expired.');
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Auto-unsubscribe from marketing when arriving from an email link
    const shouldUnsubscribe = searchParams.get('unsubscribe') === '1';
    fetchPreferences(shouldUnsubscribe);
  }, [fetchPreferences, searchParams]);

  const handleToggle = (field) => {
    setPreferences(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await apiClient.patch(`/marketing/preferences/${token}/`, {
        subscribed_marketing: preferences.subscribed_marketing,
        subscribed_restock: preferences.subscribed_restock,
      });
      setPreferences(res.data);
      setMessage('Your email preferences have been updated.');
    } catch (err) {
      console.error('[EmailPreferences] Save failed:', err.response?.status, err.response?.data);
      setError('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <FroggySpinner size="lg" label="Loading your preferences" />
          </Col>
        </Row>
      </Container>
    );
  }

  if (error && !preferences) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-5 mb-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <h2 className="mb-3">Email Preferences</h2>
          <p style={{ color: 'var(--ff-text-secondary)' }}>
            Manage what emails you receive from Feeling Froggy.
            {preferences?.email && (
              <> Preferences for <strong>{preferences.email}</strong>.</>
            )}
          </p>

          {didUnsubscribe && (
            <Alert variant="info" className="mb-3">
              You have been unsubscribed from marketing emails. You can update your preferences below.
            </Alert>
          )}

          {message && !didUnsubscribe && (
            <Alert variant="success" className="mb-3">{message}</Alert>
          )}

          {error && (
            <Alert variant="danger" className="mb-3">{error}</Alert>
          )}

          <div
            style={{
              background: 'var(--ff-surface-secondary, #f8f9fa)',
              borderRadius: 'var(--ff-radius-md, 8px)',
              padding: 'var(--ff-space-xl, 24px)',
              border: '1px solid var(--ff-border-light, #dee2e6)',
            }}
          >
            <Form>
              <Form.Check
                type="switch"
                id="marketing-toggle"
                label="Marketing emails"
                checked={preferences?.subscribed_marketing ?? false}
                onChange={() => handleToggle('subscribed_marketing')}
                className="mb-2"
              />
              <p
                className="mb-4"
                style={{
                  fontSize: 'var(--ff-font-size-sm, 0.875rem)',
                  color: 'var(--ff-text-secondary, #6c757d)',
                  marginLeft: '2.5rem',
                }}
              >
                Promotions, new arrivals, and special offers.
              </p>

              <Form.Check
                type="switch"
                id="restock-toggle"
                label="Restock notifications"
                checked={preferences?.subscribed_restock ?? false}
                onChange={() => handleToggle('subscribed_restock')}
                className="mb-2"
              />
              <p
                className="mb-4"
                style={{
                  fontSize: 'var(--ff-font-size-sm, 0.875rem)',
                  color: 'var(--ff-text-secondary, #6c757d)',
                  marginLeft: '2.5rem',
                }}
              >
                Get notified when items you're watching are back in stock.
              </p>

              <Form.Check
                type="switch"
                id="transactional-toggle"
                label="Order updates"
                checked={true}
                disabled
                className="mb-2"
              />
              <p
                className="mb-4"
                style={{
                  fontSize: 'var(--ff-font-size-sm, 0.875rem)',
                  color: 'var(--ff-text-secondary, #6c757d)',
                  marginLeft: '2.5rem',
                }}
              >
                Shipping confirmations, receipts, and order status. These cannot be disabled.
              </p>

              {/* R5 B5: Save is the page's one main act — the primary */}
              <button
                type="button"
                className="ff-btn ff-btn--primary ff-btn--block mt-2"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default EmailPreferencesPage;
